import React from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import useGraphStore from '@/store/graphStore';
import { useLanguage } from '@/context/LanguageContext';

const GraphToolbar = ({ className, onExport }) => {
  const { language } = useLanguage();
  const {
    layout,
    setLayout,
    zoom,
    zoomIn,
    zoomOut,
    fitView,
    resetView,
    undo,
    redo,
    canUndo,
    canRedo,
    refreshData,
    loading,
    showBlockchainIcons,
    toggleBlockchainIcons,
    connectMode,
    toggleConnectMode,
    neighborhoodMode,
    toggleNeighborhoodMode,
    magnetMode,
    toggleMagnetMode,
    selectedNodes,
    selectedNode,
  } = useGraphStore();
  
  const t = {
    zoomIn: language === 'de' ? 'Vergrößern' : 'Zoom In',
    zoomOut: language === 'de' ? 'Verkleinern' : 'Zoom Out',
    resetView: language === 'de' ? 'Zurücksetzen' : 'Reset',
    fitView: language === 'de' ? 'Einpassen' : 'Fit',
    layout: language === 'de' ? 'Layout' : 'Layout',
    force: language === 'de' ? 'Kraft' : 'Force',
    tree: language === 'de' ? 'Baum' : 'Tree',
    circular: language === 'de' ? 'Kreisförmig' : 'Circular',
    export: language === 'de' ? 'Exportieren' : 'Export',
    undo: language === 'de' ? 'Rückgängig' : 'Undo',
    redo: language === 'de' ? 'Wiederholen' : 'Redo',
    refresh: language === 'de' ? 'Aktualisieren' : 'Refresh',
    icons: language === 'de' ? 'Blockchain-Icons' : 'Blockchain Icons',
    connect: language === 'de' ? 'Verbinden' : 'Connect',
    neighborhood: language === 'de' ? 'Nachbarschaft' : 'Neighborhood',
    magnet: language === 'de' ? 'Magnet-Modus' : 'Magnet Mode',
  };
  
  const layouts = [
    { value: 'force', label: t.force, icon: 'fa-solid fa-diagram-project' },
    { value: 'tree', label: t.tree, icon: 'fa-solid fa-sitemap' },
    { value: 'circular', label: t.circular, icon: 'fa-solid fa-circle-nodes' },
  ];
  
  const hasSelection = selectedNodes.length > 0 || selectedNode;

  // Direct zoom handlers
  const handleZoomIn = () => {
    if (zoomIn) zoomIn();
  };
  
  const handleZoomOut = () => {
    if (zoomOut) zoomOut();
  };
  
  const handleFitView = () => {
    if (fitView) fitView();
  };
  
  const handleResetView = () => {
    resetView();
  };

  return (
    <TooltipProvider delayDuration={300}>
      <motion.div 
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className={cn(
          "flex flex-wrap items-center gap-1 p-2 bg-card/95 backdrop-blur border border-border rounded-lg shadow-lg",
          className
        )}
      >
        {/* Zoom Controls */}
        <div className="flex items-center gap-1 pr-2 border-r border-border">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-8 w-8" 
                onClick={handleZoomIn}
                disabled={zoom >= 3}
              >
                <i className="fa-solid fa-plus text-sm"></i>
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t.zoomIn}</TooltipContent>
          </Tooltip>
          
          <span className="text-xs font-mono w-12 text-center text-muted-foreground">
            {Math.round(zoom * 100)}%
          </span>
          
          <Tooltip>
            <TooltipTrigger asChild>
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-8 w-8" 
                onClick={handleZoomOut}
                disabled={zoom <= 0.5}
              >
                <i className="fa-solid fa-minus text-sm"></i>
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t.zoomOut}</TooltipContent>
          </Tooltip>
          
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={handleResetView}>
                <i className="fa-solid fa-compress text-sm"></i>
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t.resetView}</TooltipContent>
          </Tooltip>
          
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={handleFitView}>
                <i className="fa-solid fa-expand text-sm"></i>
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t.fitView}</TooltipContent>
          </Tooltip>
        </div>
        
        {/* Layout Selector */}
        <div className="flex items-center gap-1 px-2 border-r border-border">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-8 gap-2">
                <i className={layouts.find(l => l.value === layout)?.icon || 'fa-solid fa-diagram-project'}></i>
                <span className="hidden sm:inline">{t.layout}</span>
                <i className="fa-solid fa-chevron-down text-xs"></i>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {layouts.map((l) => (
                <DropdownMenuItem
                  key={l.value}
                  onClick={() => setLayout(l.value)}
                  className={cn(layout === l.value && "bg-accent")}
                >
                  <i className={cn(l.icon, "mr-2 w-4")}></i>
                  {l.label}
                  {layout === l.value && <i className="fa-solid fa-check ml-auto"></i>}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        
        {/* Premium Features */}
        <div className="flex items-center gap-1 px-2 border-r border-border">
          {/* Blockchain Icons Toggle */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant={showBlockchainIcons ? "default" : "ghost"}
                size="icon"
                className="h-8 w-8"
                onClick={toggleBlockchainIcons}
              >
                <i className="fa-solid fa-link text-sm"></i>
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t.icons}</TooltipContent>
          </Tooltip>
          
          {/* Magnet Mode Toggle */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant={magnetMode ? "default" : "ghost"}
                size="icon"
                className={cn("h-8 w-8", magnetMode && "bg-purple-600 hover:bg-purple-700")}
                onClick={toggleMagnetMode}
              >
                <i className="fa-solid fa-magnet text-sm"></i>
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t.magnet}</TooltipContent>
          </Tooltip>
          
          {/* Connect Mode Toggle */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant={connectMode ? "default" : "ghost"}
                size="icon"
                className={cn("h-8 w-8", connectMode && "bg-primary text-primary-foreground")}
                onClick={toggleConnectMode}
              >
                <i className="fa-solid fa-pen text-sm"></i>
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t.connect}</TooltipContent>
          </Tooltip>
          
          {/* Neighborhood Mode */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant={neighborhoodMode ? "default" : "ghost"}
                size="icon"
                className={cn("h-8 w-8", !hasSelection && !neighborhoodMode && "opacity-50")}
                onClick={toggleNeighborhoodMode}
                disabled={!hasSelection && !neighborhoodMode}
              >
                <i className="fa-solid fa-bullseye text-sm"></i>
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t.neighborhood}</TooltipContent>
          </Tooltip>
        </div>
        
        {/* Undo/Redo */}
        <div className="flex items-center gap-1 px-2 border-r border-border">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={undo} disabled={!canUndo()}>
                <i className="fa-solid fa-rotate-left text-sm"></i>
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t.undo}</TooltipContent>
          </Tooltip>
          
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={redo} disabled={!canRedo()}>
                <i className="fa-solid fa-rotate-right text-sm"></i>
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t.redo}</TooltipContent>
          </Tooltip>
        </div>
        
        {/* Export */}
        <div className="flex items-center gap-1 px-2 border-r border-border">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-8 gap-2">
                <i className="fa-solid fa-download"></i>
                <span className="hidden sm:inline">{t.export}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onExport?.('png')}>
                <i className="fa-solid fa-image mr-2 w-4"></i>PNG
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onExport?.('svg')}>
                <i className="fa-solid fa-vector-square mr-2 w-4"></i>SVG
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => onExport?.('json')}>
                <i className="fa-solid fa-code mr-2 w-4"></i>JSON
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onExport?.('csv')}>
                <i className="fa-solid fa-file-csv mr-2 w-4"></i>CSV
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        
        {/* Refresh */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={refreshData} disabled={loading}>
              <i className={cn("fa-solid fa-arrows-rotate text-sm", loading && "animate-spin")}></i>
            </Button>
          </TooltipTrigger>
          <TooltipContent>{t.refresh}</TooltipContent>
        </Tooltip>
      </motion.div>
    </TooltipProvider>
  );
};

export default GraphToolbar;
